import { Button } from "@athena/ui";
import { Check, Users } from "lucide-react";
import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { BulkCreateAssignmentDocument, AssignOrganizationDocument } from "../../graphql/assignments.generated";
import { OrganizationsDocument } from "../../graphql/organizations.generated";
import { CoursesDocument } from "../../graphql/courses.generated";
import { Stepper } from "@/components/Stepper";
import { StepReview } from "./StepReview";
import { StepSchedule } from "./StepSchedule";
import { StepAudience } from "./StepAudience";
import { StepCourse } from "./StepCourse";

export function AssignmentWizard({
    courseId: initialCourseId = "",
    courseTitle: initialCourseTitle = "",
    courseOrganizationId,
    showCourseStep = false,
    onClose,
    onAssigned,
}: {
    courseId?: string;
    courseTitle?: string;
    /**
     * The assigning organization when the wizard opens on a course already chosen.
     *
     * Required on that path because the courses query below is skipped without the course step,
     * so the list this component otherwise reads the organization from is never fetched.
     */
    courseOrganizationId?: string | null;
    showCourseStep?: boolean;
    onClose?: () => void;
    onAssigned?: () => void;
}) {
    const totalSteps = showCourseStep ? 4 : 3;
    const stepOffset = showCourseStep ? 1 : 0;

    const [
        step,
        setStep,
    ] = useState<number>(1);
    const [
        completed,
        setCompleted,
    ] = useState<Set<number>>(new Set());

    const [
        selectedCourseId,
        setSelectedCourseId,
    ] = useState<string>(initialCourseId);
    const [
        selectedCourseTitle,
        setSelectedCourseTitle,
    ] = useState<string>(initialCourseTitle);

    const { data: coursesData } = useQuery(CoursesDocument, { skip: !showCourseStep });
    const availableCourses = (coursesData?.courses ?? []).map((c) => ({ id: c.id, title: c.title }));
    // An assignment records who assigned it. For the pick-specific-people path that is the
    // course's own organization, which is what this wizard has always assigned as — the
    // per-organization path below assigns as the organization the operator picked instead.
    // The list is only populated when the course step is shown; opened on a known course, the
    // caller supplies the organization instead. Prefer the list so changing the course in step 1
    // changes who assigns it.
    const selectedCourseOrgId =
        (coursesData?.courses ?? []).find((c) => c.id === selectedCourseId)?.organizationId ?? courseOrganizationId;

    // Step 1: Audience
    const [
        selectedOrgs,
        setSelectedOrgs,
    ] = useState<Set<string>>(new Set());
    const [
        selectedPeople,
        setSelectedPeople,
    ] = useState<Set<string>>(new Set());

    const [
        fixedDate,
        setFixedDate,
    ] = useState<string>("");
    // const [
    //     sendNudges,
    //     setSendNudges,
    // ] = useState<boolean>(true);

    const { data: orgsData } = useQuery(OrganizationsDocument);
    const orgs = orgsData?.organizations ?? [];

    const [
        bulkCreate,
        { loading: bulkLoading },
    ] = useMutation(BulkCreateAssignmentDocument);
    const [
        assignOrg,
        { loading: orgLoading },
    ] = useMutation(AssignOrganizationDocument);

    const totalAudience = selectedOrgs.size + selectedPeople.size;
    const submitting = bulkLoading || orgLoading;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    /*
     * A due date is optional, so "none" is valid — only a date in the past is not.
     *
     * This used to be `false` without one, which made the schedule step a wall: every assignment
     * had to carry a deadline whether or not the work had one. The column has always been
     * nullable and every GraphQL `dueDate` optional; the wizard was the only thing insisting.
     */
    const fixedDateValid = !fixedDate || new Date(fixedDate + "T00:00:00") >= today;

    const audienceStep = 1 + stepOffset;
    const scheduleStep = 2 + stepOffset;
    const reviewStep = 3 + stepOffset;

    // Assigning to named people records the course's own organization as the assigner, so without
    // one there is nothing correct to send. Blocked at the button rather than thrown at submit:
    // the wizard has no error surface, so a throw here would be an unhandled rejection and the
    // dialog would simply sit there.
    const canSubmit = selectedPeople.size === 0 || !!selectedCourseOrgId;

    const canContinue =
        step === 1 && showCourseStep
            ? !!selectedCourseId
            : step === audienceStep
              ? totalAudience > 0
              : step === scheduleStep
                ? fixedDateValid
                : canSubmit;

    const dueDate = fixedDate ? new Date(fixedDate + "T00:00:00").toISOString() : undefined;

    const goNext = async () => {
        setCompleted((prev) => new Set(prev).add(step));
        if (step < totalSteps) {
            setStep((s) => s + 1);
            return;
        }
        const courseId = selectedCourseId;
        const promises: Promise<unknown>[] = [];
        if (selectedPeople.size > 0) {
            // This used to fall back to "", which the API accepted as a String! and only failed
            // on at the insert — a generic INTERNAL_SERVER_ERROR naming no field. Every assign
            // from a course page took that path, because the courses query is skipped there.
            // `canSubmit` keeps the button disabled without one; this narrows the type.
            if (!selectedCourseOrgId) return;
            promises.push(
                bulkCreate({
                    variables: {
                        input: {
                            courseId,
                            organizationId: selectedCourseOrgId,
                            userIds: Array.from(selectedPeople),
                            dueDate,
                        },
                    },
                }),
            );
        }
        for (const orgId of selectedOrgs) {
            promises.push(
                assignOrg({
                    variables: {
                        input: { courseId, organizationId: orgId, dueDate },
                    },
                }),
            );
        }
        await Promise.all(promises);
        onAssigned?.();
        onClose?.();
    };

    const goBack = () => {
        if (step > 1) setStep((s) => s - 1);
    };

    const audienceLabel = (() => {
        if (totalAudience === 0) return "No audience yet";
        if (totalAudience === 1) return "1 selected";
        return `${totalAudience} selected`;
    })();

    return (
        <div className="flex h-full flex-col">
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
                {/* Step indicator */}
                <div className="border-border mb-6 border-b pb-5">
                    <Stepper
                        current={step}
                        completed={completed}
                        steps={
                            showCourseStep
                                ? [
                                      "Course",
                                      "Audience",
                                      "Schedule",
                                      "Review",
                                  ]
                                : [
                                      "Audience",
                                      "Schedule",
                                      "Review",
                                  ]
                        }
                    />
                </div>

                {/* Step content */}
                {showCourseStep && step === 1 && (
                    <StepCourse
                        courses={availableCourses}
                        selectedCourseId={selectedCourseId}
                        onSelect={(c) => {
                            setSelectedCourseId(c.id);
                            setSelectedCourseTitle(c.title);
                        }}
                    />
                )}
                {step === audienceStep && (
                    <StepAudience
                        orgs={orgs}
                        selectedOrgs={selectedOrgs}
                        setSelectedOrgs={setSelectedOrgs}
                        selectedPeople={selectedPeople}
                        setSelectedPeople={setSelectedPeople}
                    />
                )}
                {step === scheduleStep && (
                    <StepSchedule
                        fixedDate={fixedDate ? new Date(fixedDate + "T00:00:00") : undefined}
                        setFixedDate={(d) => setFixedDate(d ? d.toISOString().slice(0, 10) : "")}
                        // sendNudges={sendNudges}
                        // setSendNudges={setSendNudges}
                    />
                )}
                {step === reviewStep && (
                    <StepReview
                        courseId={selectedCourseId}
                        courseTitle={selectedCourseTitle}
                        selectedOrgs={selectedOrgs}
                        selectedPeople={selectedPeople}
                        fixedDate={fixedDate}
                        // sendNudges={sendNudges}
                    />
                )}
            </div>

            {/* Footer actions */}
            <div className="border-border bg-background flex shrink-0 items-center justify-between border-t px-6 py-4">
                <div className="flex items-center gap-2 text-sm text-sky-500">
                    <Users className="h-4 w-4" />
                    <span>{audienceLabel}</span>
                </div>
                <div className="flex items-center gap-3">
                    {step > 1 && (
                        <Button variant="outline" onClick={goBack}>
                            Back
                        </Button>
                    )}
                    <Button onClick={goNext} disabled={!canContinue || submitting} variant={"secondary"}>
                        {step === totalSteps ? (
                            <span className="flex items-center gap-2">
                                <Check className="h-4 w-4" />
                                {submitting ? "Creating…" : "Create assignment"}
                            </span>
                        ) : (
                            "Continue"
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
